import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { TrendingUp, TrendingDown, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const fmt = (n) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(Math.round(n || 0));
const fmt2 = (n) => new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function LeagueSimulate() {
  const { league, isAdmin } = useLeague();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamOpen, setTeamOpen] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${league.league_id}/simulate/fine-campionato`);
        setData(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [league.league_id]);

  if (!isAdmin) {
    return <div className="text-slate-500 py-10 text-center">Solo l&apos;admin può accedere alla Simulazione.</div>;
  }

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1" data-testid="simulate-back-btn">
        <ArrowLeft size={12}/> Indietro
      </button>
      <div>
        <div className="text-xs tracking-[0.3em] text-purple-400 uppercase font-bold">🔮 Simulazione</div>
        <h2 className="text-3xl font-black tracking-tight">Salta al T3 · Fine Campionato</h2>
        <p className="text-slate-400 text-sm mt-1">
          Proiezione read-only del patrimonio a fine stagione usando le QA correnti. Non modifica il DB. Utile per anticipare classifica patrimoniale, plus/minus e top gainer/loser di ogni club.
        </p>
      </div>

      {loading ? (
        <div className="text-slate-500 py-10 text-center">Calcolo simulazione…</div>
      ) : !data ? null : (
        <>
          {/* KPI Lega */}
          <div className="grid grid-cols-3 gap-3">
            <KpiCard label="Monte Pagato Lega" v={fmt(data.summary.total_pagato_lega) + "cr"} color="slate"/>
            <KpiCard label="Valore Rivalutato Lega" v={fmt(data.summary.total_rivalutato_lega) + "cr"} color="emerald"/>
            <KpiCard label="Δ Lega (plus/minus)" v={(data.summary.delta_lega >= 0 ? "+" : "") + fmt(data.summary.delta_lega) + "cr"}
              color={data.summary.delta_lega >= 0 ? "emerald" : "rose"}/>
          </div>

          {/* Classifica proiettata */}
          <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 overflow-hidden shadow-2xl shadow-black/40">
            <div className="p-4 border-b border-slate-800">
              <div className="text-xs tracking-[0.3em] text-purple-400 uppercase font-bold">👑 Classifica Patrimoniale Proiettata (T3)</div>
              <div className="text-xs text-slate-400 mt-1">Ordinata per patrimonio proiettato = budget + valore rivalutato totale.</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-950/70">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-3 py-2.5">#</th>
                  <th className="px-3 py-2.5">Squadra</th>
                  <th className="px-3 py-2.5 text-right">Budget</th>
                  <th className="px-3 py-2.5 text-right">Pagato</th>
                  <th className="px-3 py-2.5 text-right">Valore Riv.</th>
                  <th className="px-3 py-2.5 text-right">Δ (%)</th>
                  <th className="px-3 py-2.5 text-right">Patrimonio T3</th>
                  <th className="px-3 py-2.5">Top ↗</th>
                  <th className="px-3 py-2.5">Top ↘</th>
                </tr>
              </thead>
              <tbody>
                {data.projections.map((t, i) => (
                  <>
                    <tr key={t.team_id} className="border-t border-slate-800 hover:bg-slate-800/30 cursor-pointer transition-colors" onClick={() => setTeamOpen(teamOpen === t.team_id ? null : t.team_id)} data-testid={`sim-row-${t.team_id}`}>
                      <td className="px-3 py-2.5 font-black text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2.5 font-bold text-slate-100">{t.team_name}<span className="text-[10px] text-slate-500 ml-1">{t.roster_count} gioc.</span></td>
                      <td className="px-3 py-2.5 text-right font-mono text-amber-300">{fmt(t.budget)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-400">{fmt(t.monte_pagato)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-cyan-300">{fmt(t.valore_rivalutato_totale)}</td>
                      <td className={`px-3 py-2.5 text-right font-mono font-black ${t.plus_minus >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {t.plus_minus >= 0 ? "+" : ""}{fmt(t.plus_minus)}
                        <span className="text-[10px] ml-1 opacity-70">({t.plus_minus_pct >= 0 ? "+" : ""}{fmt2(t.plus_minus_pct)}%)</span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono font-black text-purple-300">{fmt(t.patrimonio_proiettato)}cr</td>
                      <td className="px-3 py-2.5 text-xs text-emerald-300">{t.top_gain ? <><TrendingUp size={11} className="inline"/> {t.top_gain.name} <span className="text-[10px] opacity-70">+{fmt(t.top_gain.delta)}</span></> : "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-rose-300">{t.top_loss && t.top_loss.delta < 0 ? <><TrendingDown size={11} className="inline"/> {t.top_loss.name} <span className="text-[10px] opacity-70">{fmt(t.top_loss.delta)}</span></> : "—"}</td>
                    </tr>
                    {teamOpen === t.team_id && (
                      <tr>
                        <td colSpan={9} className="bg-slate-950/60 px-6 py-3">
                          <div className="text-xs text-slate-400 mb-2 font-bold">Breakdown giocatori · ordinato per Δ decrescente</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-72 overflow-y-auto">
                            {t.players.map(p => (
                              <div key={p.player_id} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-slate-900/50">
                                <span className="text-slate-200">{p.name}</span>
                                <div className="flex items-center gap-2 font-mono">
                                  <span className="text-slate-500">{fmt(p.prezzo_pagato)}cr</span>
                                  <span className="text-slate-600">→</span>
                                  <span className="text-cyan-300">{fmt(p.valore_rivalutato)}cr</span>
                                  <span className={`font-black ${p.delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{p.delta >= 0 ? "+" : ""}{fmt(p.delta)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-[10px] text-slate-500 text-center italic">
            💡 {data.note}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, v, color }) {
  const cmap = {
    slate: "text-slate-200 border-slate-700 bg-slate-800/30",
    emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    rose: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    purple: "text-purple-300 border-purple-500/40 bg-purple-500/10",
  };
  return (
    <div className={`p-4 rounded-3xl border ${cmap[color] || cmap.slate}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">{label}</div>
      <div className="text-3xl font-black font-mono mt-1">{v}</div>
    </div>
  );
}
